import {
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Typography,
    Grid,
    Divider,
    Box,
    List,
    ListItem,
    ListItemText,
} from "@mui/material"
import {
    LocationOn,
    Email,
    Phone,
    CalendarToday,
    Business,
    School,
    Work,
    Star,
    CheckCircle,
    Twitter,
    LinkedIn,
} from "@mui/icons-material"

const MenteeProfile = ({ menteeResponseData }) => {
    const data = menteeResponseData?.alumniMember

    const formatDate = (dateString) => {
        if (!dateString) return "Present"
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
        })
    }
    const getInitials = (name) => {
        return (
            name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase() || "U"
        )
    }

    const pastPositions = data?.workingDetails?.filter((work) => !work.present) || []

    return (
        <Box>
            <Card sx={{
                mb: 1,
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                },
                borderRadius: 2,
                mt: 4,
                border: '1px solid gray',
                // backgroundColor: 'white'
            }}>
                <CardContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: { xs: "center", sm: "flex-start" } }}>
                            <Avatar
                                src={
                                    data?.avatar?.data
                                        ? `data:image/*;base64,${data.avatar.data}`
                                        : data?.largeAvatar
                                            ? `/avatar/${data.largeAvatar}`
                                            : undefined
                                }
                                alt={data?.name}
                                sx={{
                                    width: 100,
                                    height: 100,
                                    fontSize: "2rem",
                                    bgcolor: '#028943',
                                    color: 'white'
                                }}
                            >
                                {data?.name?.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography variant="h4" component="h1" fontWeight="bold" color="#333">
                                        {data.name}
                                    </Typography>
                                    {data.verified && <CheckCircle sx={{ color: '#028943' }} fontSize="small" />}
                                    {data.starAlumni && <Star sx={{ color: "#ffc107" }} fontSize="small" />}
                                </Box>

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                                    <Chip
                                        label={data.careerStatus?.replace("_", " ")}
                                        sx={{
                                            backgroundColor: '#e8f5e9',
                                            color: '#028943',
                                            fontWeight: 500,
                                            border: 'none'
                                        }}
                                    />
                                    {data.mentor && (
                                        <Chip
                                            label="Mentor"
                                            sx={{
                                                backgroundColor: '#e8f5e9',
                                                color: '#028943',
                                                fontWeight: 500,
                                                border: 'none'
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            {/* Social Links */}
                            {data?.socialLinks && data?.socialLinks.length > 0 && (
                                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                                    {data.socialLinks.map((link, index) => (
                                        <a
                                            key={index}
                                            href={link.socialLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#028943' }}
                                        >
                                            {link.socialLinkType === "Twitter" && <Twitter />}
                                            {link.socialLinkType === "LinkedIn" && <LinkedIn />}
                                        </a>
                                    ))}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* About Me */}
            {data?.aboutMe && (
                <Card sx={{
                    mb: 3,
                    backgroundColor: 'white',
                    border: '1px solid gray'
                }}>
                    <CardHeader
                        sx={{ p: 1 }}
                        title={
                            <Typography variant="h4" sx={{ color: '#028943' }}>
                                About
                            </Typography>
                        }
                    />
                    <CardContent sx={{ p: 1 }}>
                        <Typography variant="body1" color="#666">
                            {data.aboutMe}
                        </Typography>
                    </CardContent>
                </Card>
            )
            }

            {/* Education */}
            <Card sx={{
                mb: 3,
                backgroundColor: 'white',
                border: '1px solid gray'
            }}>
                <CardHeader
                    sx={{ p: 1 }}
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <School sx={{ color: '#028943' }} />
                            <Typography variant="h4" sx={{ color: '#028943' }}>Education</Typography>
                        </Box>
                    }
                />
                <CardContent
                    sx={{ p: 1 }}
                >
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="#333">Program</Typography>
                            <Typography variant="body2" color="#666">
                                {data.programs?.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="#333">Batch</Typography>
                            <Typography variant="body2" color="#666">
                                {data.batch?.tag}
                            </Typography>
                        </Grid>
                        {/* <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="#333">Institution Type</Typography>
                            <Typography variant="body2" color="#666">
                                {data.institutionType}
                            </Typography>
                        </Grid> */}
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="#333">Campus</Typography>
                            <Typography variant="body2" color="#666">
                                {data.institutionsBranch?.name}
                            </Typography>
                        </Grid>
                    </Grid>

                    {data?.educationDetails && data?.educationDetails?.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1" sx={{ mb: 2, color: '#028943' }}>
                                Education History
                            </Typography>
                            <List disablePadding>
                                {data.educationDetails.map((edu, index) => (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            pl: 2,
                                            borderLeft: "2px solid",
                                            borderColor: '#028943',
                                            mb: 2,
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                    <Typography variant="subtitle2" color="#333">{edu.courseName}</Typography>
                                                    <Chip
                                                        label={edu.educationLevel}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#e8f5e9',
                                                            color: '#028943',
                                                            fontWeight: 500,
                                                            border: 'none'
                                                        }}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="body2" color="#666">{edu.instituteName}</Typography>
                                                    {edu.affiliation && (
                                                        <Typography variant="caption" color="#666">Affiliated to: {edu.affiliation}</Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Work Experience */}
            {
                pastPositions.length > 0 && (
                    <Card sx={{
                        mb: 3,
                        backgroundColor: 'white',
                        border: '1px solid gray'
                    }}>
                        <CardHeader
                            sx={{ p: 1 }}
                            title={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Work sx={{ color: '#028943' }} />
                                    <Typography variant="h4" sx={{ color: '#028943' }}>Work Experience</Typography>
                                </Box>
                            }
                        />
                        <CardContent
                            sx={{ p: 1 }}
                        >
                            {pastPositions?.map((work, index) => (
                                <Box
                                    key={work.id || index}
                                    sx={{
                                        pl: 2,
                                        borderLeft: "2px solid",
                                        borderColor: '#028943',
                                        mb: index < pastPositions.length - 1 ? 3 : 0,
                                    }}
                                >
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                        <Box>
                                            <Typography variant="subtitle1" color="#333">{work.designation}</Typography>
                                            <Typography variant="body2" color="#666">
                                                {work.organizationName}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: "right" }}>
                                            <Typography variant="body2" color="#666">
                                                {formatDate(work.joiningDate)} - {formatDate(work.endDate)}
                                            </Typography>

                                        </Box>
                                    </Box>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} md={12}>
                                            <Typography variant="body2" color="#666">
                                                Industry: {work.industries}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <Typography variant="body2" color="#666">
                                                Area: {work.functionalArea}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                )
            }
        </Box >
    )
}

export default MenteeProfile
